import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { AppUser } from '@/lib/auth';
import { listLeadsForUser } from '@/lib/services/leads';
import { listPropertiesForUser } from '@/lib/services/properties';
import { fetchAdminSlots, listShowings } from '@/lib/services/showings';
import {
  countPendingAvailabilityAlertEmailsForUser,
  listAvailabilityAlertSubscribersForUser,
} from '@/lib/services/availabilityAlerts';
import { listPortfolioUnitsForUser } from '@/lib/services/units';
import { fetchSiteSettings } from '@/lib/services/siteSettings';
import type { ShowingStatus } from '@/lib/types/showing';

const DEFAULT_PAGE_SIZE = 1;

export interface DashboardOverviewStats {
  properties: {
    total: number;
    withVacancy: number;
  };
  units: {
    total: number;
    published: number;
    leaseable: number;
  };
  leads: {
    total: number;
  };
  showings: {
    requested: number;
    scheduledLeads: number;
    scheduledShowings: number;
  };
  availabilityAlerts: {
    subscribersActive: number;
    pendingEmails: number;
  };
  showingAvailability: {
    availableSlots: number;
    bookedSlots: number;
  };
  siteSettings: {
    updatedAt: string | null;
  };
}

export function useDashboardOverviewStats(user: AppUser): UseQueryResult<DashboardOverviewStats> {
  const dateStart = useMemo(() => formatLocalDate(new Date()), []);

  return useQuery<DashboardOverviewStats>({
    queryKey: ['dashboard-overview-stats', { userId: user.id, dateStart }],
    queryFn: async ({ signal }) => {
      const [
        properties,
        propertiesWithVacancy,
        units,
        leads,
        showings,
        subscribers,
        pendingEmails,
        todaysSlots,
        siteSettings,
      ] = await Promise.all([
        listPropertiesForUser({
          user,
          pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
          signal,
        }),
        listPropertiesForUser({
          user,
          pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
          filters: { hasVacancy: true },
          signal,
        }),
        listPortfolioUnitsForUser({
          user,
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          signal,
        }),
        listLeadsForUser({
          user,
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          signal,
        }),
        fetchShowingsOverview({ user, dateStart, signal }),
        listAvailabilityAlertSubscribersForUser({ user, signal }),
        countPendingAvailabilityAlertEmailsForUser({ user, signal }),
        fetchAdminSlots({ user, date: dateStart, signal }),
        fetchSiteSettings({ signal }),
      ]);

      return {
        properties: {
          total: properties.totalItems,
          withVacancy: propertiesWithVacancy.totalItems,
        },
        units: {
          total: units.metrics.total,
          published: units.metrics.published,
          leaseable: units.metrics.leaseable,
        },
        leads: {
          total: leads.totalItems,
        },
        showings,
        availabilityAlerts: {
          subscribersActive: subscribers.filter((subscriber) => subscriber.is_active).length,
          pendingEmails,
        },
        showingAvailability: {
          availableSlots: todaysSlots.filter((slot) => slot.status === 'available').length,
          bookedSlots: todaysSlots.filter((slot) => slot.status === 'booked').length,
        },
        siteSettings: {
          updatedAt: siteSettings.updatedAt,
        },
      };
    },
    staleTime: 60 * 1000,
  });
}

async function fetchShowingsOverview(args: {
  user: AppUser;
  dateStart: string;
  signal?: AbortSignal;
}): Promise<DashboardOverviewStats['showings']> {
  const [requestedShowings, upcomingShowings] = await Promise.all([
    listShowings({
      user: args.user,
      filters: { status: 'requested' },
      signal: args.signal,
    }),
    listShowings({
      user: args.user,
      filters: { dateStart: args.dateStart },
      signal: args.signal,
    }),
  ]);

  const scheduledStatuses = new Set<ShowingStatus>(['confirmed', 'rescheduled']);
  const scheduledShowings = upcomingShowings.filter((entry) =>
    scheduledStatuses.has(entry.showing.status as ShowingStatus),
  ).length;
  const scheduledLeadIds = new Set(
    upcomingShowings
      .filter((entry) => scheduledStatuses.has(entry.showing.status as ShowingStatus))
      .map((entry) => entry.lead.id)
      .filter((id) => typeof id === 'string' && id.length > 0),
  );

  return {
    requested: requestedShowings.length,
    scheduledLeads: scheduledLeadIds.size,
    scheduledShowings,
  };
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear().toString();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
